import type { ApiResponse, ApiError, PaginationMeta } from '@sundkortid/shared';
import { useAuthStore } from '../stores/auth';

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

class ApiClient {
  private getToken(): string | null {
    return useAuthStore.getState().accessToken;
  }

  private async rawRequest<T>(path: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    const token = this.getToken();
    const headers: Record<string, string> = {
      ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...((options.headers as Record<string, string>) ?? {}),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      const error: ApiError = await response.json();
      throw new ApiRequestError(response.status, error.error.code, error.error.message);
    }

    return await response.json();
  }

  async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const result = await this.rawRequest<T>(path, options);
    return result.data;
  }

  async requestWithMeta<T>(path: string, options: RequestInit = {}): Promise<{ data: T; meta?: PaginationMeta }> {
    const result = await this.rawRequest<T>(path, options);
    return { data: result.data, meta: result.meta };
  }

  get<T>(path: string) {
    return this.request<T>(path);
  }

  getWithMeta<T>(path: string) {
    return this.requestWithMeta<T>(path);
  }

  post<T>(path: string, body?: unknown) {
    return this.request<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  patch<T>(path: string, body?: unknown) {
    return this.request<T>(path, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  delete<T>(path: string) {
    return this.request<T>(path, { method: 'DELETE' });
  }
}

export class ApiRequestError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

export const api = new ApiClient();
